import AddStock from '../iconsV2/AddStock'
import Administered from '../iconsV2/Administered'
import Comment from '../iconsV2/Comment'
import Disposal from '../iconsV2/Disposal'
import StockCount from '../iconsV2/StockCount'
import {
  type AdministrationStatus,
  type DiscrepancyRecord,
  type DiscrepancyStatus,
  type MediaAttachment,
  MedicationStockReason,
  type StockRecord
} from '../types'
import { getAdministrationStatusDisplay } from './administrationUtils'

export const getActionTitle = (
  reason: MedicationStockReason,
  t: (key: string) => string,
  administrationStatus: AdministrationStatus | undefined
): string => {
  switch (reason) {
    case MedicationStockReason.INITIAL_STOCK:
      return t('INITIAL_STOCK')
    case MedicationStockReason.STOCK_COUNT:
    case MedicationStockReason.RECONCILIATION:
      return t('STOCK_CHECK')
    case MedicationStockReason.DISPOSAL:
      return t('DISPOSAL')
    case MedicationStockReason.RESTOCK:
      return t('ADD_STOCK')
    case MedicationStockReason.MED_ADMIN:
      return administrationStatus ? t(administrationStatus) : t('ADMINISTERED')
    default:
      return reason.replace(/_/g, ' ')
  }
}

export const getActionSubtitle = (
  reason: MedicationStockReason,
  quantityChange: number,
  unit: string,
  notes?: string | string[],
  t?: (key: string) => string
): string | undefined => {
  switch (reason) {
    case MedicationStockReason.STOCK_COUNT:
    case MedicationStockReason.RECONCILIATION:
    case MedicationStockReason.INITIAL_STOCK:
      return undefined
    case MedicationStockReason.NOT_ADMINISTERED:
      if (Array.isArray(notes)) {
        return notes.join(', ')
      }
      return notes || ''
    case MedicationStockReason.MED_ADMIN: {
      const quantity = Math.abs(quantityChange || 0)
      if (quantity === 0 && t) {
        return t('NO_CHANGE_TO_STOCK')
      }
      return `${quantity} ${unit}`.trim()
    }
    case MedicationStockReason.DISPOSAL:
    case MedicationStockReason.RESTOCK: {
      const quantity = Math.abs(quantityChange || 0)
      return `${quantity} ${unit}`.trim()
    }
    default:
      return undefined
  }
}

export const getActionIcon = (
  type: MedicationStockReason,
  administrationStatus?: AdministrationStatus
) => {
  switch (type) {
    case MedicationStockReason.STOCK_COUNT:
    case MedicationStockReason.RECONCILIATION:
    case MedicationStockReason.INITIAL_STOCK:
      return <StockCount />
    case MedicationStockReason.DISPOSAL:
      return <Disposal />
    case MedicationStockReason.RESTOCK:
      return <AddStock />
    case MedicationStockReason.MED_ADMIN:
      return administrationStatus ? (
        getAdministrationStatusDisplay(administrationStatus).display
      ) : (
        <Administered />
      )
    case MedicationStockReason.NOT_ADMINISTERED:
      return <Comment />
    default:
      return <StockCount />
  }
}

/** An attachment shaped for antd's Upload `fileList`. */
export type AttachmentFileListItem = {
  uid: string
  name: string
  key: string
  status: 'done'
  url: string
  thumbUrl: string
}

export const convertMediaAttachmentsToFileList = (
  mediaAttachments: MediaAttachment[],
  recordId: string
): AttachmentFileListItem[] => {
  return mediaAttachments.map((attachment, i) => ({
    uid: attachment._id || `${recordId}-${i}`,
    name: attachment.key.split('/').pop() || `file-${i}`,
    key: attachment.key,
    status: 'done' as const,
    url: attachment.url,
    thumbUrl: attachment.url
  }))
}

export const transformDiscrepancyRecordToStockRecord = (
  discrepancyRecord: DiscrepancyRecord
): StockRecord => {
  const reasonToActionMap: Record<string, MedicationStockReason> = {
    STOCK_COUNT: MedicationStockReason.STOCK_COUNT,
    RECONCILIATION: MedicationStockReason.RECONCILIATION,
    RESTOCK: MedicationStockReason.RESTOCK,
    DISPOSAL: MedicationStockReason.DISPOSAL,
    INITIAL_STOCK: MedicationStockReason.INITIAL_STOCK,
    MED_ADMIN: MedicationStockReason.MED_ADMIN,
    NOT_ADMINISTERED: MedicationStockReason.NOT_ADMINISTERED
  }

  const action =
    reasonToActionMap[discrepancyRecord.reason] ||
    MedicationStockReason.STOCK_COUNT

  const stockRecord: StockRecord = {
    id: discrepancyRecord._id,
    date: discrepancyRecord.date,
    actionTime: discrepancyRecord.actionTime,
    action,
    recordedBy: {
      id: discrepancyRecord.recordedBy.id,
      name: discrepancyRecord.recordedBy.name,
      picture: discrepancyRecord.recordedBy.picture || undefined
    },
    notes: discrepancyRecord.notes,
    expectedStock: discrepancyRecord.previousStockLevel,
    stockCount: discrepancyRecord.resultingStockLevel,
    quantityChange: discrepancyRecord.quantityChange,
    unit: discrepancyRecord.unit,
    witnessed: (discrepancyRecord.witnesses?.length ?? 0) > 0,
    witnesses: discrepancyRecord.witnesses || [],
    hasDiscrepancy: true,
    discrepancy: discrepancyRecord.discrepancy
      ? {
          amount: discrepancyRecord.discrepancy.amount,
          status: discrepancyRecord.discrepancy.status as DiscrepancyStatus,
          reason: discrepancyRecord.discrepancy.reason,
          carerReason: discrepancyRecord.discrepancy.reason
        }
      : undefined,
    resultingStockLevel: discrepancyRecord.resultingStockLevel,
    previousStockLevel: discrepancyRecord.previousStockLevel,
    resident: {
      id: discrepancyRecord.resident.id,
      name: discrepancyRecord.resident.name,
      preferredName: discrepancyRecord.resident.preferredName,
      picture: discrepancyRecord.resident.picture
    },
    mediaAttachments: discrepancyRecord.mediaAttachments || [],
    changelog: discrepancyRecord.changelog
  }

  return stockRecord
}
